import type { InputHandle } from './input';
import { bodyById } from './world/bodies';
import type { BodyRow } from './world/types';

/** The buttons the cruise sim owns, in the bar's own order. */
export const CRUISE_BUTTON_IDS = ['assist-btn', 'land-btn', 'chase-btn', 'pin-btn', 'names-btn'] as const;

/** What the LAND button reads off a land verdict. */
export type LandVerdictView =
  | { ok: true; at: { bodyId: string; displayName: string; lat: number; lon: number } }
  | { ok: false; label: string; why: string };

/** The one call the LAND button makes on a mounted Atlas. */
export interface HudButtonAtlasCamera {
  shipFocus?(at: { bodyId: string; lat: number; lon: number } | null): void;
}

/** The flight assist's own switch, and whether the clock lets it act. */
export interface AssistReadout {
  on: boolean;
  /** On AND able to act, which is on at every clock rate: only the `?assistrealonly=1` control still confines it to real time and puts the button on IDLE. */
  live: boolean;
}

/** Buttons another layer adds to the bar: their ids and one repaint a frame. */
export interface HudButtonSet {
  readonly ids: readonly string[];
  update(): void;
}

export interface HudButtonsDeps {
  input: InputHandle;
  toggleAssist: () => void;
  assist: () => AssistReadout;
  attemptLand: () => void;
  recentreChase: () => void;
  /** Look back to the nose, zoom back to the default distance, pin off. */
  resetChase: () => void;
  setCameraPin: (on: boolean) => void;
  chaseOffNose: () => boolean;
  /** True while the camera is off the nose or zoomed off its default distance. */
  chaseOffDefault: () => boolean;
  cameraPinBodyId: () => string | null;
  landTarget: () => LandVerdictView;
  atlasCamera: () => HudButtonAtlasCamera | null;
  namesOn: () => boolean;
  setNames: (on: boolean, source: 'button' | 'key') => void;
  /** A fight owns the camera, so CHASE, PIN and NAMES stand down for its length. */
  inFight: () => boolean;
  /** Live reads: main.ts still owns these and they move under the buttons. */
  readonly cameraPinOn: boolean;
  readonly landOpen: boolean;
  readonly bodyRows: readonly BodyRow[];
}

export interface HudButtonsHandle {
  /** Every face repainted, the cruise set's and then each added set's. */
  update(): void;
  add(set: HudButtonSet): void;
  /** The ids of every button wired, cruise first. */
  ids(): string[];
  updateLandButton(): void;
  updatePinButton(): void;
}

/**
 * The HUD's cruise buttons: ASSIST, LAND, CHASE, PIN and NAMES, their click
 * wiring and each face's per-frame repaint. The game adds its own through `add`.
 */
export function createHudButtons(deps: HudButtonsDeps): HudButtonsHandle {
  const {
    input, toggleAssist, attemptLand, recentreChase, resetChase, setCameraPin,
    chaseOffDefault, cameraPinBodyId, landTarget, atlasCamera, namesOn, setNames, inFight,
  } = deps;
  const added: HudButtonSet[] = [];

  // ---------- ASSIST button ----------
  const elAssistBtn = document.getElementById('assist-btn');
  const elAssistStatus = document.getElementById('assist-status');
  elAssistBtn?.addEventListener('click', toggleAssist);
  let assistPaintKey = '';

  function updateAssistButton(): void {
    if (!elAssistBtn || !elAssistStatus) return;
    const assist = deps.assist();
    // IDLE is on-but-inert. The assist acts at every clock rate now, so only
    // the `?assistrealonly=1` control can put the ship in that state.
    const assistKey = `${assist.on ? 1 : 0}${assist.live ? 1 : 0}`;
    if (assistKey === assistPaintKey) return;
    assistPaintKey = assistKey;
    elAssistStatus.textContent = !assist.on ? 'OFF' : assist.live ? 'ON' : 'IDLE';
    elAssistBtn.classList.toggle('assist-off', !assist.on);
    elAssistBtn.classList.toggle('assist-idle', assist.on && !assist.live);
  }

  // ---------- LAND button (mirrors L key) ----------
  const elLandBtn = document.getElementById('land-btn');
  const elLandStatus = document.getElementById('land-status');
  if (elLandBtn) {
    elLandBtn.addEventListener('click', attemptLand);
  }

  function updateLandButton(): void {
    const v = landTarget();
    // The Atlas starts the ground's own reads for the body in focus, so the body
    // the ship is at and the point it is over go over whenever they move. Not
    // while a descent is running: the ladder's own body is the focus then.
    if (!deps.landOpen) {
      atlasCamera()?.shipFocus?.(v.ok
        ? { bodyId: v.at.bodyId, lat: v.at.lat, lon: v.at.lon }
        : null);
    }
    if (!elLandBtn || !elLandStatus) return;
    elLandBtn.classList.toggle('disabled', !v.ok);
    (elLandBtn as HTMLButtonElement).disabled = !v.ok;
    if (v.ok) {
      elLandStatus.textContent = v.at.displayName.toUpperCase().slice(0, 9);
      elLandBtn.title = `Descend to ${v.at.displayName} at ${v.at.lat.toFixed(1)}, `
        + `${v.at.lon.toFixed(1)} (L)`;
    } else {
      elLandStatus.textContent = v.label;
      elLandBtn.title = v.why;
    }
  }

  // ---------- CHASE VIEW button (mirrors the G key) ----------
  // The camera reset: back behind the nose at the default distance, pin off.
  // On screen only while the camera is off that pose - looked round or zoomed -
  // since pressing it anywhere else does nothing. C is the lighter key beside
  // it, the look back to the nose with the zoom kept. A fight owns its own
  // camera, so both are off for the length of one.
  const elChaseBtn = document.getElementById('chase-btn');
  if (elChaseBtn) {
    elChaseBtn.addEventListener('click', () => { if (!inFight()) resetChase(); });
  }
  input.on('chaseView', () => { if (!inFight()) recentreChase(); });
  input.on('cameraReset', () => { if (!inFight()) resetChase(); });
  input.on('pin', () => { if (!inFight()) setCameraPin(!deps.cameraPinOn); });

  function updateChaseButton(): void {
    if (!elChaseBtn) return;
    elChaseBtn.classList.toggle('hidden', inFight() || !chaseOffDefault());
  }

  // ---------- CAMERA PIN button ----------
  // Beside CHASE VIEW, which it is the standing form of: that one points the
  // camera back down the nose once, this one holds a body in the middle of the
  // frame until it is switched off. A fight owns its own camera, so it is off
  // for the length of one.
  const elPinBtn = document.getElementById('pin-btn');
  const elPinStatus = document.getElementById('pin-status');
  if (elPinBtn) {
    elPinBtn.addEventListener('click', () => { if (!inFight()) setCameraPin(!deps.cameraPinOn); });
  }

  // Painted only when the face changes: this runs every frame, and a textContent
  // or title written on an unchanged button dirties the node for style and layout
  // on each one.
  let pinPaintKey = '';
  function updatePinButton(): void {
    if (!elPinBtn) return;
    const fight = inFight();
    const id = deps.cameraPinOn ? cameraPinBodyId() : null;
    const key = `${fight}|${deps.cameraPinOn}|${id ?? ''}`;
    if (key === pinPaintKey) return;
    pinPaintKey = key;
    elPinBtn.classList.toggle('hidden', fight);
    elPinBtn.classList.toggle('pin-off', !deps.cameraPinOn);
    if (!elPinStatus) return;
    if (!deps.cameraPinOn) {
      elPinStatus.textContent = 'OFF';
      elPinBtn.title = PIN_TITLE_OFF;
      return;
    }
    const row = id === null ? undefined : bodyById(deps.bodyRows, id);
    elPinStatus.textContent = row ? row.display_name.toUpperCase().slice(0, 9) : 'NO BODY';
    elPinBtn.title = row
      ? `Centered on ${row.display_name}. Click to hand the camera back to the nose.`
      : 'No picked target or nearby body to hold.';
  }
  const PIN_TITLE_OFF = 'Keep the picked target, or the body you orbit, centered in the view. (P)';

  // ---------- NAMES button (mirrors the N key) ----------
  // Beside PIN and in its look. The names are hidden in a fight, so the
  // switch is too.
  const elNamesBtn = document.getElementById('names-btn');
  const elNamesStatus = document.getElementById('names-status');
  if (elNamesBtn) {
    elNamesBtn.addEventListener('click', () => { if (!inFight()) setNames(!namesOn(), 'button'); });
  }
  input.on('names', () => { if (!inFight()) setNames(!namesOn(), 'key'); });

  let namesPaintKey = '';
  function updateNamesButton(): void {
    if (!elNamesBtn) return;
    const fight = inFight();
    const isOn = namesOn();
    const key = `${fight}|${isOn}`;
    if (key === namesPaintKey) return;
    namesPaintKey = key;
    elNamesBtn.classList.toggle('hidden', fight);
    elNamesBtn.classList.toggle('names-off', !isOn);
    elNamesBtn.setAttribute('aria-pressed', isOn ? 'true' : 'false');
    if (elNamesStatus) elNamesStatus.textContent = isOn ? 'ON' : 'OFF';
  }

  function update(): void {
    updateAssistButton();
    updateLandButton();
    updateChaseButton();
    updatePinButton();
    updateNamesButton();
    for (const set of added) set.update();
  }

  return {
    update,
    add: (set) => { added.push(set); },
    ids: () => [...CRUISE_BUTTON_IDS, ...added.flatMap((s) => s.ids)],
    updateLandButton,
    updatePinButton,
  };
}
